import { folderTypes } from "@redotech/redo-model/views/folders";
import { getViewNameValidationError } from "@redotech/redo-model/views/views";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import {
  RedoModal,
  RedoModalButtonProps,
} from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { memo, useState } from "react";
import { ConfirmDeleteOmsFolderModal } from "./confirm-delete-oms-folder-modal";

export const EditOmsViewFolderModal = memo(function EditOmsViewFolderModal({
  isOpen,
  onClose,
  onSubmit,
  selectedFolder,
  deleteFolder,
  reservedFolderNames,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (folderId: string, folderName: string) => Promise<void>;
  selectedFolder: folderTypes["fulfillment-groups"];
  deleteFolder: (folderId: string) => Promise<void>;
  reservedFolderNames: Set<string>;
}) {
  const [folderName, setFolderName] = useState(selectedFolder.name);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const validationError = getViewNameValidationError(folderName);
  const isDuplicate = reservedFolderNames.has(folderName.trim());
  const submitDisabled: string | false =
    validationError ??
    (isDuplicate ? "A folder with this name already exists" : false);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await onSubmit(selectedFolder._id, folderName);
      onClose();
    } finally {
      setIsSubmitting(false);
      setFolderName("");
    }
  };

  const primaryButton: RedoModalButtonProps = {
    text: "Save",
    onClick: handleSubmit,
    disabled: submitDisabled,
    pending: false,
    size: "sm",
  };

  const secondaryButton: RedoModalButtonProps = {
    text: "Delete",
    onClick: () => setShowConfirmDelete(true),
    disabled: false,
    theme: "destructive",
    size: "sm",
  };

  return (
    <>
      <RedoModal
        isOpen={isOpen}
        modalSize="sm"
        onModalCloseRequested={isSubmitting ? () => {} : onClose}
        primaryButton={primaryButton}
        secondaryButton={secondaryButton}
        title="Edit folder"
      >
        <div data-devinid="folder-name-input">
          <RedoTextInput
            label="Folder name"
            setValue={setFolderName}
            size="sm"
            state={isSubmitting ? "readonly" : "default"}
            value={folderName}
          />
        </div>
      </RedoModal>
      <ConfirmDeleteOmsFolderModal
        folderName={selectedFolder.name}
        isOpen={showConfirmDelete && !!selectedFolder}
        onClose={() => setShowConfirmDelete(false)}
        onConfirm={async () => {
          if (selectedFolder) {
            await deleteFolder(selectedFolder._id);
            onClose();
          }
        }}
      />
    </>
  );
});
